import type { Cart, CartItem } from "@/types"
import { cartLineFor, catalogItemIdOf } from "./cart-lines"
import type { CatalogRef, EvaluationReason, RejectedCoupon, Violation } from "./types"

/** What the customer can do, right from the checkout, about one thing blocking a coupon. */
export type BlockerAction =
  | { kind: "remove-line"; cartLineId: string }
  | { kind: "set-line-quantity"; cartLineId: string; quantity: number }
  | { kind: "add-item"; ref: CatalogRef; quantity: number }
  | { kind: "review-line"; cartLineId: string }
  | { kind: "browse-menu" }

/** One plain-language thing that keeps a coupon from applying, tied to the bag line it is about. */
export type CouponBlocker = {
  key: string
  text: string
  cartLineId?: string
  lineName?: string
  action?: BlockerAction
}

/**
 * How a rejected coupon reads. `blocked` can still apply once the bag changes
 * (it stays on the list and re-evaluates), `dropped` lost to a better
 * combination the server kept, and `invalid` will not apply to this order at
 * all (it leaves the list).
 */
export type CouponNotice =
  | { kind: "blocked"; code: string; title: string; blockers: CouponBlocker[] }
  | { kind: "dropped"; code: string; note: string }
  | { kind: "invalid"; code: string; message: string }

const KNOWN_REASONS: ReadonlySet<string> = new Set<EvaluationReason>([
  "EVALUATION_REASON_NOT_FOUND",
  "EVALUATION_REASON_INACTIVE",
  "EVALUATION_REASON_EXPIRED",
  "EVALUATION_REASON_OUTSIDE_AVAILABILITY",
  "EVALUATION_REASON_USAGE_LIMIT_REACHED",
  "EVALUATION_REASON_ALREADY_USED_BY_CUSTOMER",
  "EVALUATION_REASON_NOT_ASSIGNED_TO_CUSTOMER",
  "EVALUATION_REASON_FIRST_PURCHASE_ONLY",
  "EVALUATION_REASON_MIN_PURCHASE_NOT_MET",
  "EVALUATION_REASON_MISSING_REQUIRED_ITEM",
  "EVALUATION_REASON_MISSING_REQUIRED_OPTION",
  "EVALUATION_REASON_FORBIDDEN_ITEM",
  "EVALUATION_REASON_FORBIDDEN_OPTION",
  "EVALUATION_REASON_MIN_QUANTITY_NOT_MET",
  "EVALUATION_REASON_NO_TARGET_IN_CART",
  "EVALUATION_REASON_NOT_COMBINABLE",
  "EVALUATION_REASON_MAX_COUPONS_EXCEEDED",
  "EVALUATION_REASON_CANCELLED",
])

function isKnownReason(reason: string): reason is EvaluationReason {
  return KNOWN_REASONS.has(reason)
}

const INVALID_COPY: Partial<Record<EvaluationReason, (code: string) => string>> = {
  EVALUATION_REASON_NOT_FOUND: (code) => `Não achamos o cupom ${code}. Dá uma conferida no código.`,
  EVALUATION_REASON_INACTIVE: (code) => `O cupom ${code} não está ativo no momento.`,
  EVALUATION_REASON_EXPIRED: (code) => `O cupom ${code} já expirou.`,
  EVALUATION_REASON_OUTSIDE_AVAILABILITY: (code) => `O cupom ${code} não vale neste dia ou horário.`,
  EVALUATION_REASON_USAGE_LIMIT_REACHED: (code) => `O cupom ${code} esgotou.`,
  EVALUATION_REASON_ALREADY_USED_BY_CUSTOMER: (code) => `Você já usou o cupom ${code}.`,
  EVALUATION_REASON_NOT_ASSIGNED_TO_CUSTOMER: (code) => `O cupom ${code} não está disponível para a sua conta.`,
  EVALUATION_REASON_FIRST_PURCHASE_ONLY: (code) => `O cupom ${code} vale só na primeira compra nesta loja.`,
  EVALUATION_REASON_CANCELLED: (code) => `O cupom ${code} foi cancelado pela loja.`,
}

function unknownReasonMessage(code: string): string {
  return `O cupom ${code} não pode ser usado neste pedido.`
}

/** Why a code will not apply to any order right now, in the customer's words; an unknown reason gets a generic line. */
export function invalidCouponMessage(code: string, reason: string): string {
  const copy = isKnownReason(reason) ? INVALID_COPY[reason] : undefined
  return copy ? copy(code) : unknownReasonMessage(code)
}

/** Whether the bag can never satisfy this reason, so the code should leave the list. */
export function isInvalidReason(reason: string): boolean {
  return !isKnownReason(reason) || reason in INVALID_COPY
}

function missingUnits(violation: Violation): number {
  return Math.max(1, violation.requiredQuantity - violation.actualQuantity)
}

function refName(ref: CatalogRef | undefined): string {
  return ref?.name?.trim() ?? ""
}

function lineName(line: CartItem | undefined): string {
  return line?.item?.name?.trim() ?? ""
}

function scopeLineOf(cart: Cart, ref: CatalogRef | undefined): CartItem | undefined {
  if (!ref) return undefined
  return cart.items.find(
    (line) =>
      (!!ref.scopeItemId && catalogItemIdOf(line) === ref.scopeItemId) ||
      (!!ref.scopeItemExternalCode && line.item?.externalCode === ref.scopeItemExternalCode),
  )
}

function missingText(violation: Violation): string {
  const units = missingUnits(violation)
  const verb = units === 1 ? "Falta" : "Faltam"
  const name = refName(violation.ref)
  if (violation.ref?.kind === "REF_KIND_CATEGORY") {
    const noun = units === 1 ? "item" : "itens"
    return name ? `${verb}: ${units} ${noun} de ${name}` : `${verb}: ${units} ${noun}`
  }
  return name ? `${verb}: ${units} ${name}` : `${verb}: ${units} ${units === 1 ? "item" : "itens"}`
}

function addAction(violation: Violation): BlockerAction | undefined {
  if (violation.ref?.kind === "REF_KIND_ITEM") {
    return { kind: "add-item", ref: violation.ref, quantity: missingUnits(violation) }
  }
  return { kind: "browse-menu" }
}

/** The plain PT-BR line for one violation, with the bag line it is about and what fixes it. */
export function describeViolation(violation: Violation, cart: Cart, key: string): CouponBlocker {
  const line = cartLineFor(cart, violation.lineId)
  const onLine = line ? { cartLineId: line.id, lineName: lineName(line) } : {}
  const name = refName(violation.ref)

  switch (violation.reason) {
    case "EVALUATION_REASON_MISSING_REQUIRED_ITEM":
    case "EVALUATION_REASON_MIN_QUANTITY_NOT_MET": {
      const action: BlockerAction | undefined = line
        ? { kind: "set-line-quantity", cartLineId: line.id, quantity: line.quantity + missingUnits(violation) }
        : addAction(violation)
      return { key, text: missingText(violation), ...onLine, action }
    }
    case "EVALUATION_REASON_MISSING_REQUIRED_OPTION": {
      const owner = line ?? scopeLineOf(cart, violation.ref)
      const ownerName = lineName(owner)
      const needs = name ? `do complemento ${name}` : "de um complemento"
      const text = ownerName
        ? `${ownerName} precisa ${needs}`
        : name
          ? `Falta o complemento ${name}`
          : "Falta escolher um complemento"
      return {
        key,
        text,
        ...(owner ? { cartLineId: owner.id, lineName: ownerName } : {}),
        action: owner ? { kind: "review-line", cartLineId: owner.id } : { kind: "browse-menu" },
      }
    }
    case "EVALUATION_REASON_FORBIDDEN_ITEM": {
      const target = name || lineName(line) || "um item da sacola"
      const text = violation.ref?.kind === "REF_KIND_CATEGORY" ? `Remova os itens de ${target}` : `Remova: ${target}`
      return { key, text, ...onLine, action: line ? { kind: "remove-line", cartLineId: line.id } : undefined }
    }
    case "EVALUATION_REASON_FORBIDDEN_OPTION": {
      const owner = line ?? scopeLineOf(cart, violation.ref)
      return {
        key,
        text: `Remova: ${name || "um complemento"}`,
        ...(owner ? { cartLineId: owner.id, lineName: lineName(owner) } : {}),
        action: owner ? { kind: "review-line", cartLineId: owner.id } : undefined,
      }
    }
    case "EVALUATION_REASON_NO_TARGET_IN_CART":
      return {
        key,
        text: name
          ? `O desconto vale para ${name}, que não está na sacola`
          : "Nenhum item da sacola recebe este desconto",
        action: addAction(violation),
      }
    case "EVALUATION_REASON_MIN_PURCHASE_NOT_MET":
      return { key, text: "O pedido ainda não chegou ao valor mínimo deste cupom", action: { kind: "browse-menu" } }
    default:
      return { key, text: "Algo na sacola impede este cupom", ...onLine }
  }
}

function droppedNote(reason: EvaluationReason, maxCoupons: number): string {
  if (reason === "EVALUATION_REASON_MAX_COUPONS_EXCEEDED") {
    return maxCoupons <= 1
      ? "Este pedido aceita 1 cupom. Mantivemos o de maior desconto."
      : `Este pedido aceita até ${maxCoupons} cupons. Mantivemos os de maior desconto.`
  }
  return "Não combina com os outros cupons do pedido. Mantivemos o de maior desconto."
}

/**
 * How a coupon the server rejected reads to the customer. Every known reason
 * has its own copy; an unknown one falls back to a generic line instead of
 * leaking a code. Only the codes the customer entered are ever described —
 * nothing here suggests a coupon they did not type.
 */
export function describeRejection(rejected: RejectedCoupon, cart: Cart, maxCoupons: number): CouponNotice {
  const { code, reason } = rejected
  if (reason === "EVALUATION_REASON_NOT_COMBINABLE" || reason === "EVALUATION_REASON_MAX_COUPONS_EXCEEDED") {
    return { kind: "dropped", code, note: droppedNote(reason, maxCoupons) }
  }
  if (isInvalidReason(reason)) return { kind: "invalid", code, message: invalidCouponMessage(code, reason) }
  const violations: Violation[] =
    rejected.violations.length > 0
      ? rejected.violations
      : [{ reason, ruleId: "", lineId: "", requiredQuantity: 0, actualQuantity: 0 }]
  return {
    kind: "blocked",
    code,
    title: `O cupom ${code} ainda não vale`,
    blockers: violations.map((violation, index) => describeViolation(violation, cart, `${code}-${index}`)),
  }
}
